import { Client } from 'xrpl';
import { API_BASE_URL } from './config';

export class XamanAuth {
    private client: Client;
    private userAddress: string | null = null;
    private pollingInterval: ReturnType<typeof setInterval> | null = null;
    private pollingReject: ((reason?: any) => void) | null = null;

    constructor() {
        this.client = new Client('wss://xrplcluster.com');

        // Restore session from localStorage
        this.restoreSession();
    }

    /**
     * Restore session from localStorage
     */
    private restoreSession(): void {
        const savedAddress = localStorage.getItem('xaman_wallet_address');
        if (savedAddress) {
            this.userAddress = savedAddress;
            console.log('✅ Session restored:', savedAddress);
        }
    }

    /**
     * Save session to localStorage
     */
    private saveSession(address: string): void {
        localStorage.setItem('xaman_wallet_address', address);
        console.log('💾 Session saved');
    }

    /**
     * Clear session from localStorage
     */
    private clearSession(): void {
        localStorage.removeItem('xaman_wallet_address');
        localStorage.removeItem('xaman_user_token');
        localStorage.removeItem('selectedNFT');
        // Clear any wallet-specific profile data
        const keys = Object.keys(localStorage);
        keys.forEach(key => {
            if (key.startsWith('userProfile_')) {
                localStorage.removeItem(key);
            }
        });
        console.log('🗑️ Session cleared');
    }

    /**
     * Initiate login with Xaman wallet
     */
    async login(): Promise<{ success: boolean; address?: string; error?: string }> {
        try {
            console.log('📱 Creating Xaman SignIn payload via backend...');

            // Create payload via backend
            const createResponse = await fetch(`${API_BASE_URL}/xaman/auth/signin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' }
            });

            const createData = await createResponse.json();

            if (!createData.success) {
                throw new Error(createData.message || 'Failed to create payload');
            }

            console.log('✅ Payload created:', createData.uuid);
            console.log('QR Code:', createData.qrUrl);
            console.log('Deep Link:', createData.deepLink);

            // Show QR code modal
            this.showQRModal(createData.qrUrl, createData.deepLink);

            // Poll for payload status
            const result = await this.pollPayloadStatus(createData.uuid);

            // Close QR modal
            this.closeQRModal();

            if (result.signed && result.account) {
                this.userAddress = result.account;
                this.saveSession(result.account); // Save session
                // Store Xaman user_token for push-based signing (auto-detect wallet)
                if (result.user_token) {
                    localStorage.setItem('xaman_user_token', result.user_token);
                }
                await this.connectClient();
                console.log('✅ Login successful! Wallet:', this.userAddress);
                return { success: true, address: this.userAddress || undefined };
            }

            return { success: false, error: 'User rejected the sign-in request' };
        } catch (error) {
            this.closeQRModal();
            console.error('Xaman login error:', error);
            return { success: false, error: 'Login failed. Please try again.' };
        }
    }

    /**
     * Poll payload status until resolved
     */
    private async pollPayloadStatus(uuid: string): Promise<any> {
        console.log('⏳ Waiting for user to sign in Xaman app...');

        return new Promise((resolve, reject) => {
            const maxAttempts = 60; // 5 minutes (60 * 5 seconds)
            let attempts = 0;

            this.pollingReject = reject;
            this.pollingInterval = setInterval(async () => {
                try {
                    attempts++;

                    const response = await fetch(`${API_BASE_URL}/xaman/payload/${uuid}`);
                    const data = await response.json();

                    if (data.success && data.payload.resolved) {
                        this.pollingReject = null; // Don't reject on natural resolve
                        this.stopPolling();
                        console.log('Payload resolved:', data.payload);
                        resolve(data.payload);
                    } else if (attempts >= maxAttempts) {
                        this.pollingReject = null;
                        this.stopPolling();
                        reject(new Error('Timeout waiting for signature'));
                    }
                } catch (error) {
                    this.pollingReject = null;
                    this.stopPolling();
                    reject(error);
                }
            }, 5000); // Poll every 5 seconds
        });
    }

    /**
     * Stop any active polling interval
     */
    private stopPolling(): void {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
        if (this.pollingReject) {
            this.pollingReject(new Error('User closed modal'));
            this.pollingReject = null;
        }
    }

    /**
     * Connect to XRPL
     */
    private async connectClient(): Promise<void> {
        if (!this.client.isConnected()) {
            await this.client.connect();
        }
    }

    /**
     * Get user's NFTs from the Aliens On XRPL collection
     * Uses Horizon Launchpad API — returns NFTs with metadata already resolved
     */
    async getUserNFTs(): Promise<any[]> {
        if (!this.userAddress) {
            throw new Error('User not logged in');
        }

        try {
            // Check cache first (5 minute cache)
            const CACHE_VERSION = 'v10'; // v10: Horizon Launchpad API
            const cacheKey = `nfts_${this.userAddress}_${CACHE_VERSION}`;
            const cached = this.getCachedData(cacheKey, 5 * 60 * 1000);
            if (cached && cached.length > 0) {
                console.log('✅ Using cached NFTs');
                return cached;
            }

            console.log('📡 Fetching NFTs via Horizon Launchpad...');

            const response = await fetch(`${API_BASE_URL}/nft/horizon-nfts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ classic_address: this.userAddress })
            });

            const data = await response.json();

            if (!response.ok || data.detail) {
                throw new Error(data.detail || 'Failed to fetch NFTs');
            }

            const allNFTs = data.nfts || [];
            console.log(`Found ${allNFTs.length} total NFTs`);

            // Filter to "Aliens On XRPL" collection only
            const COLLECTION_NAME = 'Aliens On XRPL';
            const alienNFTs = allNFTs
                .filter((nft: any) => nft.collection_name === COLLECTION_NAME)
                .map((nft: any) => ({
                    ...nft,
                    metadata: {
                        name: nft.name,
                        description: nft.description,
                        image: nft.image_url,
                        image_url: nft.image_url,
                        attributes: nft.attributes,
                        collection: { name: nft.collection_name },
                        properties: nft.properties,
                        external_url: nft.external_url,
                    },
                    imageUrl: nft.image_url || ''
                }));

            console.log(`✅ Loaded ${alienNFTs.length} Aliens On XRPL NFTs`);

            // Cache the results
            this.setCachedData(cacheKey, alienNFTs);

            return alienNFTs;
        } catch (error) {
            console.error('Error fetching NFTs:', error);
            throw error;
        }
    }

    /**
     * Fetch NFT metadata from IPFS via backend proxy (avoids CORS issues)
     */
    private async fetchNFTMetadataWithTimeout(uri: string, timeout: number = 10000): Promise<any> {
        try {
            console.log(`📡 Fetching via backend proxy: ${uri}`);

            // Create timeout promise
            const timeoutPromise = new Promise((_, reject) => {
                setTimeout(() => reject(new Error('Metadata fetch timeout')), timeout);
            });

            // Use backend proxy to fetch IPFS data
            const response = await Promise.race([
                fetch(`${API_BASE_URL}/ipfs/proxy?url=${encodeURIComponent(uri)}`),
                timeoutPromise
            ]) as Response;

            if (response.ok) {
                const result = await response.json();
                if (result.success && result.data) {
                    console.log('✅ Metadata fetched successfully via proxy');
                    return result.data;
                }
            }

            console.error('Proxy fetch failed for:', uri);
            return null;
        } catch (error) {
            console.error('Error fetching metadata via proxy:', error);
            return null;
        }
    }

    /**
     * Fetch NFT metadata from IPFS (legacy method for compatibility)
     */
    private async fetchNFTMetadata(uri: string): Promise<any> {
        return this.fetchNFTMetadataWithTimeout(uri, 5000);
    }

    /**
     * Get cached data from localStorage
     */
    private getCachedData(key: string, maxAge: number): any | null {
        try {
            const cached = localStorage.getItem(key);
            if (!cached) return null;

            const { data, timestamp } = JSON.parse(cached);
            const age = Date.now() - timestamp;

            if (age > maxAge) {
                localStorage.removeItem(key);
                return null;
            }

            return data;
        } catch (error) {
            console.error('Cache read error:', error);
            return null;
        }
    }

    /**
     * Set cached data in localStorage
     */
    private setCachedData(key: string, data: any): void {
        try {
            const cacheObj = {
                data,
                timestamp: Date.now()
            };
            localStorage.setItem(key, JSON.stringify(cacheObj));
        } catch (error) {
            console.error('Cache write error:', error);
        }
    }

    /**
     * Convert hex to string
     */
    private hexToString(hex: string): string {
        let str = '';
        for (let i = 0; i < hex.length; i += 2) {
            str += String.fromCharCode(parseInt(hex.substr(i, 2), 16));
        }
        return str;
    }

    /**
     * Convert IPFS URL to HTTP gateway URL (using public gateway without rate limits)
     */
    private convertIPFSUrl(ipfsUrl: string): string {
        if (!ipfsUrl) return '';
        if (ipfsUrl.startsWith('ipfs://')) {
            // Use dweb.link - a public gateway with better rate limits for images
            return ipfsUrl.replace('ipfs://', 'https://dweb.link/ipfs/');
        }
        return ipfsUrl;
    }

    /**
     * Clear NFT cache (useful for debugging or forcing refresh)
     */
    clearNFTCache(): void {
        if (!this.userAddress) return;

        // Clear all versions of NFT cache
        const keys = Object.keys(localStorage);
        const nftCacheKeys = keys.filter(key => key.startsWith(`nfts_${this.userAddress}`));
        nftCacheKeys.forEach(key => localStorage.removeItem(key));

        console.log('🗑️ NFT cache cleared');
    }

    /**
     * Logout user
     */
    logout(): void {
        this.userAddress = null;
        this.clearSession(); // Clear session
        if (this.client.isConnected()) {
            this.client.disconnect();
        }
    }

    /**
     * Get current user address
     */
    getAddress(): string | null {
        return this.userAddress;
    }

    /**
     * Check if user is logged in
     */
    isLoggedIn(): boolean {
        return this.userAddress !== null;
    }

    /**
     * Show QR code modal
     */
    private showQRModal(qrCodeUrl: string, deepLink: string): void {
        // Remove any existing modal
        this.closeQRModal();

        const modal = document.createElement('div');
        modal.id = 'xaman-qr-modal';
        modal.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.9);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 100000;
            animation: fadeIn 0.3s ease;
        `;

        modal.innerHTML = `
            <div style="
                background: linear-gradient(135deg, #0a0e27 0%, #1a1e3f 100%);
                border: 2px solid var(--primary, #00ff88);
                border-radius: 20px;
                padding: 40px;
                max-width: 500px;
                text-align: center;
                position: relative;
                box-shadow: 0 20px 60px rgba(0, 255, 136, 0.3);
            ">
                <button id="close-qr-modal" style="
                    position: absolute;
                    top: 15px;
                    right: 15px;
                    background: transparent;
                    border: none;
                    color: #fff;
                    font-size: 30px;
                    cursor: pointer;
                    transition: transform 0.3s ease;
                ">×</button>

                <h2 style="
                    color: var(--primary, #00ff88);
                    font-family: 'Orbitron', sans-serif;
                    margin-bottom: 20px;
                    font-size: 24px;
                ">🔐 Scan with Xaman</h2>

                <p style="
                    color: #a0a0a0;
                    font-family: 'Orbitron', sans-serif;
                    margin-bottom: 30px;
                    font-size: 14px;
                ">Scan this QR code with your Xaman mobile app</p>

                <div style="
                    background: white;
                    padding: 20px;
                    border-radius: 15px;
                    display: inline-block;
                    margin-bottom: 20px;
                ">
                    <img src="${qrCodeUrl}" alt="Xaman QR Code" style="
                        width: 300px;
                        height: 300px;
                        display: block;
                    ">
                </div>

                <div style="margin-top: 20px;">
                    <a href="${deepLink}" target="_blank" style="
                        display: inline-block;
                        background: linear-gradient(135deg, var(--primary, #00ff88), var(--secondary, #00d4ff));
                        color: #0a0e27;
                        padding: 12px 30px;
                        border-radius: 25px;
                        text-decoration: none;
                        font-family: 'Orbitron', sans-serif;
                        font-weight: 600;
                        transition: transform 0.3s ease;
                    " onmouseover="this.style.transform='scale(1.05)'" onmouseout="this.style.transform='scale(1)'">
                        📱 Open in Xaman App
                    </a>
                </div>

                <p style="
                    color: #666;
                    font-family: 'Orbitron', sans-serif;
                    margin-top: 30px;
                    font-size: 12px;
                ">Waiting for approval...</p>
            </div>
        `;

        // Add animation
        const style = document.createElement('style');
        style.textContent = `
            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }
        `;
        document.head.appendChild(style);

        // Close on background click or button click
        modal.addEventListener('click', (e) => {
            if (e.target === modal) {
                this.closeQRModal();
            }
        });

        const closeBtn = modal.querySelector('#close-qr-modal');
        closeBtn?.addEventListener('click', () => this.closeQRModal());

        document.body.appendChild(modal);
    }

    /**
     * Close QR code modal
     */
    private closeQRModal(): void {
        const modal = document.getElementById('xaman-qr-modal');
        if (modal) {
            modal.remove();
        }
        this.stopPolling();
    }
}
